<template>
  <section class="content-collection">
    <div class="results-details">
      <div class="result-title">
        <h1 class="title clamp-1">{{ title }}</h1>
      </div>
      <div class="results-actions">
        <div
          v-if="userAuthStore.features.FILTER_COLLECTIONS && fcDelete && filterCollection"
          class="action"
        >
          <filter-collection-delete-button
            :filter-collection-id="filterCollection.id"
            @delete-cancel="handleDeleteCancel"
            @delete-confirm="handleDeleteConfirm"
            @deleted="emit('filter-collection-deleted', $event)"
          />
        </div>
        <div
          v-if="userAuthStore.features.FILTER_COLLECTIONS && fcEdit && filterCollection"
          class="action"
        >
          <filter-collection-form-button
            :entity-type="filterCollection.type"
            :filter-collection="filterCollection"
            :disabled="isDeleteConfirm"
            @saved="emit('filter-collection-updated', $event)"
          />
        </div>
        <div
          v-if="userAuthStore.features.FILTER_COLLECTIONS && fcCopy && filterCollection"
          class="action"
        >
          <filter-collection-copy-button
            :filter-collection="filterCollection"
            :disabled="isDeleteConfirm"
            @copied="emit('filter-collection-copied', $event)"
          />
        </div>
        <div
          v-if="userAuthStore.features.FILTER_COLLECTIONS && fcShare && filterCollection"
          class="action"
        >
          <filter-collection-share-button
            :filter-collection="filterCollection"
            :disabled="isDeleteConfirm || filterCollection.privacy === 'private'"
          />
        </div>
        <div v-if="viewAll" class="action">
          <router-link :to="viewAll" class="btn -material view-all" @click="selfView">
            <i class="material-symbols-outlined fill btn-ico" aria-hidden="true">grid_view</i>
            <span class="btn-txt">View All</span>
          </router-link>
        </div>
      </div>
    </div>
    <div class="results-cards">
      <slot />
    </div>
  </section>
</template>

<script setup lang="ts">
import type { FilterCollection } from '@/types/model-types'
import { useUserAuthStore } from '@/stores/user-auth'
import FilterCollectionFormButton from '@/components/buttons/filter-collections/filter-collection-form-button.vue'
import { ref } from 'vue'
import FilterCollectionDeleteButton from '@/components/buttons/filter-collections/filter-collection-delete-button.vue'
import FilterCollectionShareButton from '@/components/buttons/filter-collections/filter-collection-share-button.vue'
import FilterCollectionCopyButton from '@/components/buttons/filter-collections/filter-collection-copy-button.vue'
import { fbApi } from '@/utils/http-api'

/** PROPS **/
const props = withDefaults(
  defineProps<{
    title: string
    fcDelete: boolean
    fcEdit: boolean
    fcCopy: boolean
    fcShare: boolean
    viewAll?: string
    filterCollection?: FilterCollection | null
  }>(),
  {
    fcDelete: false,
    fcEdit: false,
    fcCopy: false,
    fcShare: false,
    viewAll: '',
    filterCollection: null,
  },
)

/** EMITS **/
const emit = defineEmits([
  'filter-collection-updated',
  'filter-collection-deleted',
  'filter-collection-copied',
])

/** STATE **/
const userAuthStore = useUserAuthStore()
const isDeleteConfirm = ref(false)

/** METHODS **/
function handleDeleteCancel() {
  isDeleteConfirm.value = false
}

function handleDeleteConfirm() {
  isDeleteConfirm.value = true
}

async function selfView() {
  // nope
  if (!props.filterCollection) {
    return
  }

  // skip if fb collection
  if (props.filterCollection.id === 0 || props.filterCollection.userId === 0) {
    return
  }

  // skip if user doesn't own
  if (props.filterCollection.userId !== userAuthStore.user.id) {
    return
  }

  try {
    await fbApi.put(`/filter-collections/${props.filterCollection.id}/self-view`, {
      skipErrorModal: true,
    })
  } catch (err) {
    console.error('Failed to track self view', err)
  }
}
</script>
